// Tabbed scene views (contract section 7.4) and the move-replay chrome. The provider owns the shown
// tab: it auto-focuses once per step when every lit target lives in one tab (a manual choice wins
// until the next step), follows replay to the tab with the most changed objects, and maps keys to
// tabs. Only the shown tab's bricks draw; badges count lit targets in every tab.

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Views } from '../presentation/types';
import { usePlay } from '../play/PlayController';
import { BrickHost } from '../bricks/host';
import { useScene } from './SceneContext';
import { materialBackground, materialInk } from '../theme/materials';
import { ArtImage } from '../art/ArtImage';
import { Slot } from '../components/ObjectCard';
import { usePresentation } from '../presentation/PresentationContext';
import { TurnBanner } from '../play/Overlays';
import { busiestTab, countByTab, countChoicesByTab, eligibleTabs, expandTabs, focusTab, navigationEntries, navigationObjectCount, shownTab, tabZones } from './tabs';
import { ReplayProvider, ViewsContext, useReplay, useViews, type ReplayApi, type ViewsApi } from './ViewContext';
import { useGameDisplay } from '../display/GameDisplay';
import { activityTab, TABLE_ACTIVITY } from '../display/activity';
import type { DisplayedMotion } from '../boards/runtime/motion';
import { diff } from '../motion/diff';
import { FLOAT_H, FLOAT_W, deltaPlace } from '../motion/deltaPlace';

export { ReplayProvider, useReplay, useViews, type ReplayApi, type ViewsApi };

const typing = (e: KeyboardEvent) => !!(e.target as HTMLElement | null)?.closest?.('input, textarea, select, [contenteditable="true"]');

export function ViewsProvider({ views, children }: { views: Views | undefined; children: React.ReactNode }) {
  const display = useGameDisplay(), activityRoot = useRef<HTMLSpanElement>(null);
  const { table } = useScene();
  const play = usePlay();
  const replay = useReplay();
  const allTabs = useMemo(() => (views ? expandTabs(views, table) : []), [views, table]);
  const zonesByTab = useMemo(() => tabZones(allTabs, table), [allTabs, table]);
  const lit = play?.lit;
  // Conditional eligibility affects navigation/panels, never the allTabs map.
  const tabs = useMemo(() => eligibleTabs(allTabs, zonesByTab, table, lit ?? []), [allTabs, lit, zonesByTab, table]);
  const [current, setCurrent] = useState<string | null>(null);
  const [manual, setManual] = useState<string | null>(null);
  const remembered = useRef(new Map<string, string>());
  const previousGroup = views?.tabs.find(t => t.groupLabel && current?.startsWith(`${t.id}:`))?.id ?? null;
  const shown = shownTab(tabs, current, previousGroup);
  const navigation = navigationEntries(tabs, shown, remembered.current);
  const activity = useRef({ display, play, replay, zonesByTab, table }); activity.current = { display, play, replay, zonesByTab, table };
  useLayoutEffect(() => {
    const root = activityRoot.current?.previousElementSibling;
    if (!root) return;
    const follow = (event: Event) => {
      const motion = (event as CustomEvent<DisplayedMotion>).detail, current = activity.current;
      if (!motion || motion.reset || document.hidden || !current.display?.followTabs || !current.display.mayFollow() || current.replay?.stop || current.play?.isMyTurn || current.play?.active || current.play?.inspected) return;
      const tab = activityTab(diff(motion.previous, motion.next), current.zonesByTab, motion.next, motion.previous);
      if (tab) setCurrent(tab);
    };
    root.addEventListener(TABLE_ACTIVITY, follow);
    return () => root.removeEventListener(TABLE_ACTIVITY, follow);
  }, [views !== undefined]);
  useEffect(() => {
    const tab = tabs.find(t => t.key === shown);
    if (tab?.groupLabel) remembered.current.set(tab.id, tab.key);
  }, [shown, tabs]);

  // Auto-focus once per step; a manual choice made during this step wins until the next one.
  const stepKey = play?.stepKey ?? '';
  useEffect(() => {
    if (!views || views.autoFocus === 'none' || replay?.stop || !lit) return;
    if (manual === stepKey) return;
    const tab = focusTab(lit, zonesByTab, table, new Set(allTabs.filter(t => t.passive).map(t => t.key)), shown);
    if (tab) setCurrent(tab);
    // Only a new step re-runs focus; lit anchors of the same step do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey, views, replay?.stop]);

  // Replay shows the tab holding most of the changed objects.
  const replayRev = replay?.stop?.frame.revision ?? null;
  useEffect(() => {
    if (replayRev === null || !replay?.marks) return;
    const tab = busiestTab(replay.marks.focus, zonesByTab, table);
    if (tab) setCurrent(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayRev]);

  const api = useMemo<ViewsApi>(() => ({
    tabs, navigation, current: shown, material: views?.material ?? 'parchment', ...(views?.tint ? { tint: views.tint } : {}), ...(views?.badges ? { badges: views.badges } : {}),
    select: key => { if (tabs.some(t => t.key === key)) { display?.manual(); setCurrent(key); setManual(stepKey); } },
  }), [tabs, navigation, shown, stepKey, views]);

  useEffect(() => {
    if (!tabs.length) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || typing(e)) return;
      const entry = navigation.find(t => t.hotkey === e.key.toLowerCase());
      if (entry) { e.preventDefault(); api.select(entry.leafKey); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigation, api]);

  return <ViewsContext.Provider value={views ? api : null}>{children}<span ref={activityRoot} hidden /></ViewsContext.Provider>;
}

/** The tab bar and the shown tab's bricks, drawn in the views' area. */
export function ViewArea() {
  const ariaPrefix = useId();
  const views = useViews();
  const presentation = usePresentation();
  const { table, banner, log } = useScene();
  const play = usePlay();
  const zonesByTab = useMemo(() => tabZones(views?.tabs ?? [], table), [views, table]);
  // `badges: "choices"` counts each lit choice once per tab while idle; mid-step the options are the lit anchors.
  const choiceBadges = views?.badges === 'choices';
  const counts = useMemo(() => (choiceBadges && play && !play.active ? countChoicesByTab(play.targets.anchors, zonesByTab, table) : countByTab(play?.lit ?? [], zonesByTab, table)), [choiceBadges, play?.active, play?.targets, play?.lit, zonesByTab, table]);
  const stripRef = useRef<HTMLDivElement | null>(null);
  const [stripScrolls, setStripScrolls] = useState(false);
  useLayoutEffect(() => {
    const strip = stripRef.current;
    if (!strip || !choiceBadges || typeof ResizeObserver === 'undefined') { setStripScrolls(false); return; }
    const measure = () => setStripScrolls(strip.scrollWidth - strip.clientWidth - strip.scrollLeft > 4);
    measure(); strip.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure); observer.observe(strip);
    return () => { strip.removeEventListener('scroll', measure); observer.disconnect(); };
  });
  // Conditional tabs can all disappear (including during replay). Navigation must
  // remain available even when there is no tab panel to contain the banner.
  if (!views || !views.current) return views ? <ReplayBanner /> : null;
  const tab = views.tabs.find(t => t.key === views.current)!;
  const navigation = views.navigation ?? navigationEntries(views.tabs, views.current);
  const entry = navigation.find(t => t.children.some(c => c.key === tab.key))!;
  // Ordinal IDs avoid collisions between distinct allowed punctuation in seat IDs.
  const panelId = `${ariaPrefix}-view-panel-${navigation.indexOf(entry)}`;
  const childLabelId = `${panelId}-child-label`;
  const ink = materialInk(views.material, views.tint);
  const parchment = { background: materialBackground(views.material, views.tint), '--table-ink': ink.ink, '--table-ink-muted': ink.muted, color: ink.ink } as React.CSSProperties;
  return (
    <section className={`scene-views${choiceBadges ? ' has-choice-badges' : ''}`} aria-label="Table views">
      <ReplayBanner />
      <div ref={stripRef} className={`view-tabs${stripScrolls ? ' can-scroll' : ''}`} role="tablist" aria-label="Table views">
        {navigation.map((t, i) => {
          const lit = t.children.reduce((n, c) => n + (counts.get(c.key) ?? 0), 0);
          const on = t.children.some(c => c.key === views.current);
          // A tab that only shows the log counts its lines.
          const logTab = t.children.some(c => c.bricks.some(b => b.brick === 'log-feed'));
          const muted = logTab ? Math.min(log.length, 99) : navigationObjectCount(t, zonesByTab, table);
          return (
            <button key={t.key} type="button" role="tab" id={`${ariaPrefix}-view-tab-${i}`} aria-selected={on} aria-controls={on ? panelId : undefined}
              className={`view-tab${on ? ' is-on' : ''}${lit ? ' has-targets' : ''}`} data-view={t.key} tabIndex={on ? 0 : -1} style={on ? parchment : undefined}
              title={t.description} aria-description={t.description}
              {...(t.hotkey ? { 'aria-keyshortcuts': t.hotkey } : {})} onClick={() => views.select(t.leafKey)}
              onKeyDown={e => {
                if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
                e.preventDefault(); e.stopPropagation();
                const next = navigation[(i + (e.key === 'ArrowRight' ? 1 : navigation.length - 1)) % navigation.length]!;
                views.select(next.leafKey);
                (e.currentTarget.parentElement?.querySelector(`[data-view="${CSS.escape(next.key)}"]`) as HTMLElement | null)?.focus();
              }}>
              {t.hotkey && <kbd className="view-key" aria-hidden="true">{t.hotkey}</kbd>}
              {t.icon && presentation.icon(t.icon) && <span className="view-icon" aria-hidden="true"><Slot value={presentation.icon(t.icon)!} /></span>}
              <span className="view-label">{t.label}</span>
              {lit > 0
                ? <span className="view-badge is-lit">{lit}{t.badge ? <span className="view-badge-noun"> {t.badge}</span> : ''}</span>
                : choiceBadges ? null : <span className="view-badge" aria-label={`${muted} ${logTab ? 'entries' : 'items'}`}>{muted}</span>}
            </button>
          );
        })}
      </div>
      {tab.groupLabel && <label className="view-seat-selector">View: {entry.label}
        <select aria-label={`View: ${entry.label}`} value={tab.key} onChange={e => views.select(e.target.value)}>
          {entry.children.map(c => <option key={c.key} value={c.key}>{c.label} ({counts.get(c.key) ?? navigationObjectCount({ ...entry, children: [c] }, zonesByTab, table)}{(counts.get(c.key) ?? 0) > 0 ? ` ${c.badge ?? 'targets'}` : ' items'})</option>)}
        </select>
      </label>}
      <div className={`view-panel view-panel-${tab.id}${tab.background ? ' has-scene-art' : ''}`} style={parchment} role="tabpanel" id={panelId} aria-labelledby={`${ariaPrefix}-view-tab-${navigation.indexOf(entry)}`} aria-describedby={tab.groupLabel ? childLabelId : undefined}>
        {tab.groupLabel && <h2 className="view-child-label" id={childLabelId}>{tab.label}</h2>}
        {tab.background && <ArtImage source={tab.background} className="view-background" />}
        {tab.bricks.map((inst, i) => <BrickHost key={`${tab.key}-${i}`} inst={inst} seat={tab.seat} />)}
      </div>
      {banner && <TurnBanner key={banner.key} text={banner.text} color={banner.color} />}
    </section>
  );
}

// ---- Replay chrome ------------------------------------------------------------------------

/** The blue banner with Older, Newer and Back to live, plus Left/Right/Escape. */
export function ReplayBanner() {
  const replay = useReplay();
  const play = usePlay();
  const stop = replay?.stop ?? null;
  useEffect(() => {
    if (!stop || !replay) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || typing(e)) return;
      if (e.key === 'Escape' && !play?.drawerOpen && !play?.inspected) { e.preventDefault(); replay.exit(); }
      else if (e.key === 'ArrowLeft' && stop.older !== null) { e.preventDefault(); replay.enter(stop.older); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); if (stop.newer !== null) replay.enter(stop.newer); else replay.exit(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stop, replay, play?.drawerOpen, play?.inspected]);
  if (!stop || !replay) return null;
  const n = stop.movesAgo;
  return (
    <div className="replay-banner" role="status" aria-label="Move replay">
      <span className="replay-dot" aria-hidden="true" />
      <span className="replay-text">Replaying: {n} {n === 1 ? 'move' : 'moves'} ago</span>
      <button type="button" className="replay-button" disabled={stop.older === null} onClick={() => stop.older !== null && replay.enter(stop.older)} aria-keyshortcuts="ArrowLeft">‹ Older</button>
      <button type="button" className="replay-button" disabled={stop.newer === null} onClick={() => stop.newer !== null && replay.enter(stop.newer)} aria-keyshortcuts="ArrowRight">Newer ›</button>
      <button type="button" className="replay-button is-live" onClick={replay.exit} aria-keyshortcuts="Escape">Back to live</button>
    </div>
  );
}

interface Spot { key: string; left: number; top: number; width: number; height: number; text: string; kind: 'callout' | 'ghost' | 'chip-gain' | 'chip-loss'; below?: boolean; cx?: number; cy?: number }

/** Callout, ghosts and +/- chips drawn over the past position, measured from the drawn table. */
export function ReplayOverlay() {
  const replay = useReplay();
  const views = useViews();
  const ref = useRef<HTMLDivElement | null>(null);
  const [spots, setSpots] = useState<Spot[]>([]);
  const marks = replay?.stop ? replay.marks : null;
  useLayoutEffect(() => {
    const root = ref.current?.closest('.scene-root');
    if (!marks || !root) { setSpots([]); return; }
    let frame = 0;
    const measure = () => {
      const box = root.getBoundingClientRect();
      const rectOf = (selector: string) => {
        const el = root.querySelector(selector);
        const r = el?.getBoundingClientRect();
        return r && r.width > 0 && r.height > 0 ? r : null;
      };
      const id = (attr: string, v: string) => `[${attr}="${CSS.escape(v)}"]`;
      const out: Spot[] = [];
      const at = (r: DOMRect) => ({ left: r.left - box.left, top: r.top - box.top, width: r.width, height: r.height });
      if (marks.primary) {
        const r = rectOf(id('data-motion-id', marks.primary));
        // Above the change, unless that would cover the tab bar or leave the top of the table.
        const panel = root.querySelector('.view-panel')?.getBoundingClientRect();
        const below = r ? r.top - (panel && r.top >= panel.top ? panel.top : box.top + 60) < 56 : false;
        if (r) out.push({ key: 'callout', ...at(r), text: marks.callout, kind: 'callout', below });
      }
      for (const g of marks.ghosts) {
        const r = g.host ? rectOf(id('data-motion-id', g.host)) : g.zone ? rectOf(id('data-motion-zone', g.zone)) : null;
        if (r && g.title) out.push({ key: `ghost-${g.id}`, ...at(r), text: g.title, kind: 'ghost' });
      }
      // A +/- chip never covers the digits it reports or a header: a strip's counters send theirs to the free end of the strip's header row, others sit above their counter (see deltaPlace).
      const lanes = new Map<Element, number>();
      for (const c of marks.counters) {
        const el = root.querySelector(id('data-motion-counter', c.id)), r = rectOf(id('data-motion-counter', c.id));
        if (el && r) { const p = deltaPlace(el, r, lanes, window.innerWidth); out.push({ key: `c-${c.id}`, ...at(r), text: c.text, kind: c.gain ? 'chip-gain' : 'chip-loss', cx: p.left + FLOAT_W / 2 - box.left, cy: p.top + FLOAT_H / 2 - box.top }); }
      }
      for (const z of marks.zones) {
        const r = rectOf(`li${id('data-motion-zone', z.id)}, .hud ${id('data-motion-zone', z.id)}, .brick-zone${id('data-motion-zone', z.id)} > .brick-title`);
        // (at the right end of the heading's row, not over its text)
        if (r) out.push({ key: `z-${z.id}`, ...at(r), text: z.text, kind: z.gain ? 'chip-gain' : 'chip-loss', cx: Math.min(r.right + 22, box.right - 26) - box.left, cy: r.top + r.height / 2 - box.top });
      }
      setSpots(out);
    };
    measure();
    const again = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    window.addEventListener('resize', again);
    window.addEventListener('scroll', again, true);
    const late = setTimeout(again, 60);
    return () => { cancelAnimationFrame(frame); clearTimeout(late); window.removeEventListener('resize', again); window.removeEventListener('scroll', again, true); };
  }, [marks, views?.current]);
  return (
    <div ref={ref} className="replay-overlay" aria-hidden={spots.length ? undefined : true}>
      {spots.map(s => {
        if (s.kind === 'callout') return (
          <div key={s.key} className={`replay-callout${s.below ? ' is-below' : ''}`} role="note" style={{ left: s.left + s.width / 2, top: s.below ? s.top + s.height : s.top }}>{s.text}</div>
        );
        if (s.kind === 'ghost') return (
          <div key={s.key} className="replay-ghost" style={{ left: s.left + 4, top: s.top + 4, maxWidth: Math.max(80, s.width - 8) }} title={`Was here: ${s.text}`}>
            <span aria-hidden="true">↗</span> {s.text}
          </div>
        );
        return <span key={s.key} className={`replay-chip ${s.kind === 'chip-gain' ? 'is-gain' : 'is-loss'}`} style={{ left: s.cx ?? s.left + s.width - 6, top: s.cy ?? s.top - 6 }}>{s.text}</span>;
      })}
    </div>
  );
}
